import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PlatformApiError,
  type ConversationDetail,
  type ConversationPage,
  type Viewer,
} from '@sfc/sdk';
import { platform } from '../api/client';
import { DetailPane, Split } from '../ui/DetailPane';
import { Empty } from '../ui/Empty';
import { dateTime, relativeTime } from '../ui/format';
import { Icon } from '../ui/Icon';
import { StatStrip } from '../ui/StatStrip';

const AGENT_LABELS: Record<string, string> = {
  match: 'Match',
  scope: 'Scope',
  guide: 'Guide',
};

/**
 * What visitors asked the website assistant.
 *
 * Read and erase, and nothing else. There is no reply box because there is
 * nobody to reply to: the visitor was anonymous and closed the tab. The words
 * are rendered as text — React escapes them, and nothing here turns a visitor's
 * message into markup.
 */
export function ConversationsPanel({ viewer }: { viewer: Viewer }): React.JSX.Element {
  const organizationId = viewer.organization.id;
  const [page, setPage] = useState<ConversationPage>();
  const [detail, setDetail] = useState<ConversationDetail>();
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<string>();
  const detailRequest = useRef<AbortController | null>(null);

  const mayErase = viewer.capabilities.includes('conversations:delete');

  const load = useCallback(
    async (before?: string, signal?: AbortSignal) => {
      setBusy(true);
      setProblem(undefined);
      try {
        setPage(await platform.conversations.list(organizationId, { before }, signal));
      } catch (error) {
        if (!signal?.aborted) setProblem(describe(error));
      } finally {
        if (!signal?.aborted) setBusy(false);
      }
    },
    [organizationId],
  );

  useEffect(() => {
    const controller = new AbortController();
    setPage(undefined);
    setDetail(undefined);
    void load(undefined, controller.signal);
    return () => {
      controller.abort();
      detailRequest.current?.abort();
    };
  }, [load]);

  const stats = useMemo(() => {
    const items = page?.conversations ?? [];
    const turns = items.reduce((total, entry) => total + entry.messageCount, 0);
    return [
      { label: 'On this page', value: items.length },
      { label: 'Turns in total', value: turns },
      {
        label: 'Asked once and left',
        value: items.filter((entry) => entry.messageCount <= 1).length,
      },
    ];
  }, [page]);

  async function open(id: string) {
    detailRequest.current?.abort();
    const controller = new AbortController();
    detailRequest.current = controller;
    setDetail(undefined);
    setProblem(undefined);
    setConfirming(undefined);
    try {
      setDetail(await platform.conversations.detail(organizationId, id, controller.signal));
    } catch (error) {
      if (!controller.signal.aborted) setProblem(describe(error));
    }
  }

  async function erase(id: string) {
    setProblem(undefined);
    try {
      await platform.conversations.erase(organizationId, id);
      setDetail(undefined);
      setConfirming(undefined);
      await load();
    } catch (error) {
      setProblem(describe(error));
    }
  }

  return (
    <section className="card" aria-labelledby="section-heading">
      <div className="toolbar">
        <span className="toolbar__spacer" />
        <button className="button" disabled={busy} onClick={() => void load()}>
          <Icon name="refresh" size={16} />
          Refresh conversations
        </button>
      </div>

      <StatStrip stats={stats} testId="conversation-stats" />

      {problem ? (
        <p className="notice notice--problem" role="alert">
          {problem}
        </p>
      ) : null}
      {busy && !page ? (
        <p className="loading" role="status">
          Loading conversations…
        </p>
      ) : null}

      <Split open={Boolean(detail)}>
        <div className="split__list">
          {page?.conversations.length === 0 ? (
            <Empty
              icon="conversations"
              title="No conversations yet"
              hint="Questions visitors ask the assistant on the website arrive here."
            />
          ) : null}

          <ul className="ledger" aria-label="Assistant conversations">
            {page?.conversations.map((entry) => (
              <li
                key={entry.id}
                className={`ledger__row ledger__row--inbox${detail?.id === entry.id ? ' ledger__row--selected' : ''}`}
                onClick={() => void open(entry.id)}
              >
                <span className="avatar avatar--square" aria-hidden="true">
                  <Icon name="conversations" size={16} />
                </span>
                <div className="ledger__id">
                  <span className="row__title row__title--clamp">{entry.opening || 'No question was asked'}</span>
                  <span className="row__sub">
                    {AGENT_LABELS[entry.agentId] ?? entry.agentId} agent, {entry.messageCount}{' '}
                    {entry.messageCount === 1 ? 'turn' : 'turns'}
                  </span>
                </div>
                <div className="ledger__meta">
                  <time className="row__sub" dateTime={entry.lastMessageAt} title={dateTime(entry.lastMessageAt)}>
                    {relativeTime(entry.lastMessageAt)}
                  </time>
                </div>
                <button
                  className="button button--small"
                  aria-label={`Read the conversation that began ${entry.opening || 'with no question'}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    void open(entry.id);
                  }}
                >
                  Read
                </button>
              </li>
            ))}
          </ul>

          {page?.nextBefore ? (
            <button className="button pager" disabled={busy} onClick={() => void load(page.nextBefore!)}>
              Older conversations
            </button>
          ) : null}
        </div>

        {detail ? (
          <DetailPane
            label="Conversation transcript"
            recordKey={detail.id}
            title={`${AGENT_LABELS[detail.agentId] ?? detail.agentId} agent`}
            subtitle={
              <>
                Started {dateTime(detail.startedAt)}, <span data-testid="conversation-turns">{detail.messageCount}</span>{' '}
                {detail.messageCount === 1 ? 'turn' : 'turns'}
              </>
            }
            closeLabel="Close transcript"
            onClose={() => setDetail(undefined)}
          >
            <ol className="transcript" aria-label="Turns, oldest first">
              {detail.messages.map((message) => (
                <li
                  key={message.position}
                  className={`bubble bubble--${message.role === 'visitor' ? 'visitor' : 'assistant'}`}
                >
                  <span className="bubble__who">{message.role === 'visitor' ? 'Visitor' : 'Assistant'}</span>
                  <p className="bubble__text">{message.content}</p>
                </li>
              ))}
            </ol>

            {detail.messages.length === 0 ? (
              <p className="notice" role="status">
                This conversation has no turns stored.
              </p>
            ) : null}

            {mayErase ? (
              <div className="danger-zone">
                {confirming === detail.id ? (
                  <>
                    <p className="row__sub">This cannot be undone.</p>
                    <span className="button-row">
                      <button className="button button--small" onClick={() => setConfirming(undefined)}>
                        Keep it
                      </button>
                      <button className="button button--danger button--small" onClick={() => void erase(detail.id)}>
                        Erase permanently
                      </button>
                    </span>
                  </>
                ) : (
                  <>
                    <p className="row__sub">The visitor was anonymous; erasing removes every turn.</p>
                    <button className="button button--danger button--small" onClick={() => setConfirming(detail.id)}>
                      Erase this conversation
                    </button>
                  </>
                )}
              </div>
            ) : null}
          </DetailPane>
        ) : null}
      </Split>
    </section>
  );
}

function describe(error: unknown): string {
  return error instanceof PlatformApiError
    ? error.message
    : 'The conversations could not be loaded. Please try again.';
}
